import React, { useLayoutEffect, useRef, useState, type CSSProperties } from "react";

/**
 * SVG の viewBox を利用してテキストを親コンテナに自動フィットさせるコンポーネント。
 *
 * 仕組み:
 *  1. `<svg>` の中に `<text>` を配置する（font-size は親 CSS から継承する）
 *  2. 初回レンダー直後（useLayoutEffect）に `getBBox()` でテキストの実サイズを計測する
 *  3. viewBox のサイズは「テキスト bbox」と「コンテナ実寸」の max で決める
 *      → テキストがコンテナに収まる場合、viewBox = コンテナサイズ → scale=1（自然サイズを保持し、
 *        親 CSS の font-size を尊重して**拡大しない**）
 *      → テキストがはみ出す場合、viewBox = bbox → `preserveAspectRatio="xMidYMid meet"`
 *        によりネイティブに縮小される（shrink-to-fit）
 *  4. ResizeObserver で svg のサイズ変化を監視し、コンテナが後から広がった場合でも
 *     再計測して shrink-only を維持する。
 *
 * 特徴:
 *  - `font-size` / `line-height` / `font-family` / `color` などは親 CSS で指定する
 *  - **拡大は行わず、必要な場合のみ縮小する**（"shrink-only" セマンティクス）
 *  - ブラウザのネイティブスケーリングを使うため、大→小のちらつきが発生しない
 *  - 複数行（`\n` 区切り）は `<tspan>` に分割し、親の `line-height` を計算に利用する
 *
 * 制限:
 *  - HTML タグはネストできない（プレーンテキスト前提）
 */
interface FitTextProps {
  children: string;
  className?: string;
  style?: CSSProperties;
  /** アクセシビリティ用のラベル。指定がなければ children をそのまま使う。 */
  ariaLabel?: string;
}

const DEFAULT_LINE_HEIGHT_RATIO = 1.2;

/**
 * 親 CSS の line-height を em 比率に換算する。
 * "normal" や解析失敗時は DEFAULT_LINE_HEIGHT_RATIO を返す。
 *
 * テスト容易性のため export しているが、実利用は本ファイル内の measure() のみ。
 */
export const resolveLineHeightRatio = (element: Element): number => {
  const computed = window.getComputedStyle(element);
  const fontSizePx = parseFloat(computed.fontSize);
  const lineHeightRaw = computed.lineHeight;
  if (!lineHeightRaw || lineHeightRaw === "normal") return DEFAULT_LINE_HEIGHT_RATIO;
  const lineHeightPx = parseFloat(lineHeightRaw);
  if (Number.isNaN(lineHeightPx) || fontSizePx <= 0) return DEFAULT_LINE_HEIGHT_RATIO;
  return lineHeightPx / fontSizePx;
};

/**
 * bbox と rect（同一単位・CSS px 相当）から shrink-only な viewBox を計算する。
 *  - viewBox サイズ = max(bbox, rect) → scale ≤ 1 を保証（拡大しない）
 *  - viewBox 中心 = bbox 中心 → 拡大された viewBox 内でもテキストが中央に配置される
 * rect が取れない場合は bbox をそのまま使う（フォールバック）。
 */
const computeShrinkOnlyViewBox = (bbox: DOMRect, rect: DOMRect): string => {
  const hasRect = rect.width > 0 && rect.height > 0;
  const viewBoxW = hasRect ? Math.max(bbox.width, rect.width) : bbox.width;
  const viewBoxH = hasRect ? Math.max(bbox.height, rect.height) : bbox.height;
  const bboxCenterX = bbox.x + bbox.width / 2;
  const bboxCenterY = bbox.y + bbox.height / 2;
  const viewBoxX = bboxCenterX - viewBoxW / 2;
  const viewBoxY = bboxCenterY - viewBoxH / 2;
  return `${viewBoxX} ${viewBoxY} ${viewBoxW} ${viewBoxH}`;
};

const FitText: React.FC<FitTextProps> = ({ children, className, style, ariaLabel }) => {
  const svgRef = useRef<SVGSVGElement>(null);
  const textRef = useRef<SVGTextElement>(null);
  const [viewBox, setViewBox] = useState<string>("0 0 100 100");
  const [dy, setDy] = useState<string>(`${DEFAULT_LINE_HEIGHT_RATIO}em`);

  const lines = children.split("\n");

  // biome-ignore lint/correctness/useExhaustiveDependencies: children 経由で DOM の text 内容が変わり getBBox の返す寸法が変わるため、children は間接的な依存として明示的に指定する。
  useLayoutEffect(() => {
    const svg = svgRef.current;
    const node = textRef.current;
    if (!svg || !node) return undefined;

    const measure = () => {
      // 親 CSS の line-height を em 比率に換算し、tspan の dy に反映する
      setDy(`${resolveLineHeightRatio(svg)}em`);

      // 単位を揃えるための下準備:
      // getBBox() は現在の viewBox に基づいた SVG user unit を返すのに対し、
      // getBoundingClientRect() は CSS px を返す。両者を Math.max で直接比較すると
      // 単位が混在してはみ出し判定を誤る（例: viewBox がすでに縮小状態だと、実際は
      // はみ出しているテキストが「収まっている」と判定されて縮小されず切れる）。
      // そのため rect を取得できる場合は、一時的に viewBox を「0 0 rect.w rect.h」に
      // リセットして 1 user unit = 1 CSS px の状態にしてから getBBox() を呼び直す。
      // setAttribute は React の state を経由しないため余計な再レンダーは発生しない。
      // 注意: この一時 viewBox は原点が左上なので、text-anchor="middle" x="0" のテキストは
      // 左半分がクリップされる。この状態は同一 tick 内で最終 viewBox に上書きするため、
      // ユーザーには見えない想定だが、setState 側で「値が同じ」と判断されると React が
      // 再レンダーをスキップして DOM が一時 viewBox のまま残ることがある。
      // そのため最終 viewBox は setAttribute と setState の両方で反映する（下記参照）。
      const rect = svg.getBoundingClientRect();
      const previousViewBox = svg.getAttribute("viewBox");
      if (rect.width > 0 && rect.height > 0) {
        svg.setAttribute("viewBox", `0 0 ${rect.width} ${rect.height}`);
      }

      // 計測は paint 前に同期実行されるため、ユーザーはダミー viewBox 状態を目視しない。
      // ただし getBBox() は以下のケースで width/height=0 を返しうる:
      //   - children が空文字列 / 空白のみ
      //   - 親が一時的に display:none で非表示
      //   - フォント未ロード中
      //   - jsdom や SSR 環境
      // 0 サイズを viewBox に設定するとスケール計算が破綻しテキストが表示されなくなるため、
      // 有効な計測が得られた場合のみ更新し、そうでなければ前回値を維持する。
      const bbox = node.getBBox();
      if (bbox.width <= 0 || bbox.height <= 0) {
        // 一時 viewBox が DOM に残らないように直前の値へ戻す。
        if (previousViewBox !== null) {
          svg.setAttribute("viewBox", previousViewBox);
        }
        return;
      }

      // shrink-only な最終 viewBox を計算する。
      // bbox は上で viewBox をリセット済みなので、rect と同じ CSS px 相当の単位。
      const finalViewBox = computeShrinkOnlyViewBox(bbox, rect);
      // setState と同時に DOM 属性も直接更新する。ResizeObserver の再計測で同じ値が
      // 算出された場合、setState は再レンダーをスキップするため、上で書き換えた一時
      // viewBox（"0 0 W H"）が DOM に残り、テキストが左半分でクリップされる問題を防ぐ。
      svg.setAttribute("viewBox", finalViewBox);
      setViewBox(finalViewBox);
    };

    // 初回計測（children 更新時にも実行される）
    measure();

    // コンテナサイズが後から変化するケース（ウィンドウリサイズ、メディアクエリ、
    // フォント遅延ロード、兄弟要素の表示切替による flex 再配分など）でも shrink-only を
    // 維持するため、ResizeObserver で svg のサイズ変化を検知して再計測する。
    const ro = new ResizeObserver(measure);
    ro.observe(svg);
    return () => ro.disconnect();
  }, [children]);

  return (
    <svg
      ref={svgRef}
      className={className}
      style={{ width: "100%", height: "100%", display: "block", ...style }}
      viewBox={viewBox}
      preserveAspectRatio="xMidYMid meet"
      role="img"
      aria-label={ariaLabel ?? children}
    >
      <text
        ref={textRef}
        x="0"
        y="0"
        textAnchor="middle"
        dominantBaseline="hanging"
        fill="currentColor"
      >
        {lines.map((line, index) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: 行は children を \n で split した固定順序で並び替え・挿入が起きないため、index キーで安全。
          <tspan key={index} x="0" dy={index === 0 ? 0 : dy}>
            {line}
          </tspan>
        ))}
      </text>
    </svg>
  );
};

export default FitText;
